import {progressCopyPools, type ProgressCopyPhase} from './progress-personality';

interface CopyState {
  version: 1;
  turn: number;
  lastHawk: number;
  recent: string[];
  cursors: Partial<Record<ProgressCopyPhase, number>>;
}
const knownIds = new Set(Object.values(progressCopyPools).flat().map(copy => copy.id));

/** Browser-session copy rotation. Stores copy IDs only, never requests or answers. */
export class ProgressCopySession {
  private state: CopyState = {version: 1, turn: 0, lastHawk: -4, recent: [], cursors: {}};
  private persist: (state: CopyState) => void;
  private now: () => number;
  constructor(saved?: unknown, persist: (state: CopyState) => void = () => {}, now = () => Date.now()) {
    this.persist = persist; this.now = now;
    if (!saved || typeof saved !== 'object') return;
    const value = saved as Partial<CopyState>;
    if (value.version !== 1 || !Number.isSafeInteger(value.turn) || value.turn! < 0 ||
        !Number.isSafeInteger(value.lastHawk) || value.lastHawk! > value.turn! || value.lastHawk! < -4) return;
    this.state.turn = value.turn!; this.state.lastHawk = value.lastHawk!;
    this.state.recent = Array.isArray(value.recent) ? value.recent.filter(id => knownIds.has(id)).slice(-5) : [];
    for (const phase of Object.keys(progressCopyPools) as ProgressCopyPhase[]) {
      const cursor = value.cursors?.[phase];
      if (Number.isSafeInteger(cursor) && cursor! >= 0) this.state.cursors[phase] = cursor! % progressCopyPools[phase].length;
    }
  }

  createTurn() {
    let turn: number | undefined, opening: string | undefined, lastCopyAt: number | undefined, asideUsed = false;
    const used = new Set<string>();
    const choose = (phase: ProgressCopyPhase): string | null => {
      turn ??= ++this.state.turn;
      const pool = progressCopyPools[phase], start = this.state.cursors[phase] ?? 0;
      const candidates = Array.from({length: pool.length}, (_, offset) => (start + offset) % pool.length)
        .filter(index => !used.has(pool[index].id) && (!pool[index].hawk || turn! - this.state.lastHawk >= 4));
      const index = candidates.find(index => !this.state.recent.includes(pool[index].id)) ??
        candidates.reduce<number | undefined>((oldest, index) => oldest === undefined ||
          this.state.recent.indexOf(pool[index].id) < this.state.recent.indexOf(pool[oldest].id) ? index : oldest, undefined);
      if (index === undefined) return null;
      const copy = pool[index]; used.add(copy.id);
      this.state.cursors[phase] = (index + 1) % pool.length;
      this.state.recent = [...this.state.recent.filter(id => id !== copy.id), copy.id].slice(-5);
      if (copy.hawk) this.state.lastHawk = turn;
      lastCopyAt = this.now(); this.persist(this.state);
      return copy.text;
    };
    return {
      opening: () => opening ??= choose('opening')!,
      asideDelay: () => asideUsed ? null : Math.max(4800, lastCopyAt === undefined ? 0 : 9000 - (this.now() - lastCopyAt)),
      aside: (phase: Exclude<ProgressCopyPhase, 'opening'>) => {
        if (asideUsed || lastCopyAt !== undefined && this.now() - lastCopyAt < 9000) return null;
        const copy = choose(phase);
        if (copy) asideUsed = true;
        return copy;
      },
    };
  }
}
